import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth-guard';
import { adminGuard } from './core/guards/admin-guard';
import { superAdminGuard } from './core/guards/super-admin-guard';
import { companyGuard } from './core/guards/company-guard';
import { trainerGuard } from './core/guards/trainer-guard';
import { coordinatorGuard } from './core/guards/coordinator-guard';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () =>
      import('./shared/layouts/public-layout/public-layout').then(m => m.PublicLayout),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/public/landing/landing').then(m => m.Landing),
      },
      {
        path: 'privacy-policy',
        loadComponent: () =>
          import('./features/public/privacy-policy/privacy-policy').then(m => m.PrivacyPolicy),
      },
      { path: 'privacy', redirectTo: 'privacy-policy', pathMatch: 'full' },
      {
        path: 'terms-of-service',
        loadComponent: () =>
          import('./features/public/terms-of-service/terms-of-service').then(m => m.TermsOfService),
      },
      { path: 'terms', redirectTo: 'terms-of-service', pathMatch: 'full' },
      {
        path: 'refund-policy',
        loadComponent: () =>
          import('./features/public/refund-policy/refund-policy').then(m => m.RefundPolicy),
      },
      {
        path: 'contact',
        loadComponent: () =>
          import('./features/public/contact/contact').then(m => m.Contact),
      },
    ],
  },
  {
    path: 'verify-email',
    loadComponent: () =>
      import('./features/auth/verify-email/verify-email').then(m => m.VerifyEmail),
  },
  {
    path: 'oauth2/redirect',
    loadComponent: () =>
      import('./features/auth/oauth2-redirect/oauth2-redirect').then(m => m.OAuth2Redirect),
  },
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./features/auth/login/login').then(m => m.Login),
      },
      {
        path: 'company-register',
        loadComponent: () =>
          import('./features/auth/company-register/company-register').then(m => m.CompanyRegister),
      },
      {
        path: 'company-login',
        loadComponent: () =>
          import('./features/auth/company-login/company-login').then(m => m.CompanyLogin),
      },
      {
        path: 'trainer-login',
        loadComponent: () =>
          import('./features/auth/trainer-login/trainer-login').then(m => m.TrainerLogin),
      },
      {
        path: 'coordinator-login',
        loadComponent: () =>
          import('./features/auth/coordinator-login/coordinator-login').then(m => m.CoordinatorLogin),
      },
      {
        path: 'coordinator-forgot-password',
        loadComponent: () =>
          import('./features/auth/coordinator-forgot-password/coordinator-forgot-password').then(m => m.CoordinatorForgotPassword),
      },
      {
        path: 'coordinator-set-password',
        loadComponent: () =>
          import('./features/auth/coordinator-set-password/coordinator-set-password').then(m => m.CoordinatorSetPassword),
        data: { mode: 'set' },
      },
      {
        path: 'coordinator-reset-password',
        loadComponent: () =>
          import('./features/auth/coordinator-set-password/coordinator-set-password').then(m => m.CoordinatorSetPassword),
        data: { mode: 'reset' },
      },
      {
        path: 'register',
        redirectTo: '/auth/login',
        pathMatch: 'full',
      },
      {
        path: 'oauth2/redirect',
        loadComponent: () =>
          import('./features/auth/oauth2-redirect/oauth2-redirect').then(m => m.OAuth2Redirect),
      },
      {
        path: 'verify-email',
        loadComponent: () =>
          import('./features/auth/verify-email/verify-email').then(m => m.VerifyEmail),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./features/auth/reset-password/reset-password').then(m => m.ResetPassword),
      },
    ],
  },
  // ── Public referrer profile — no auth required ───────────────────
  {
    path: 'user/profile/:username',
    loadComponent: () =>
      import('./features/user/public-profile/public-profile').then(m => m.PublicProfile),
  },
  // ── Problems list — uses user-layout for navbar ──────────────────────────
  {
    path: 'problems',
    loadComponent: () =>
      import('./shared/layouts/user-layout/user-layout').then(m => m.UserLayout),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/user/problems/problems').then(m => m.Problems),
      },
    ],
  },
  // ── Problem solve — full screen, no layout header ─────────────────────────
  { path: 'problems/:slug', redirectTo: 'problems/:slug/description', pathMatch: 'full' },
  {
    path: 'problems/:slug/description',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/user/problem-solve/problem-solve').then(m => m.ProblemSolve),
  },
  // ── Trainer live-interview problem solve — full screen, opens in a new tab ──
  {
    path: 'trainer/solve/:problemId',
    canActivate: [trainerGuard],
    loadComponent: () =>
      import('./features/trainer/trainer-problem-solve/trainer-problem-solve').then(m => m.TrainerProblemSolve),
  },
  // ── Admin portal — obscure URL, not linked from anywhere public ──
  {
    path: 'rx-portal/b7f3e2a9',
    loadComponent: () =>
      import('./features/auth/admin-portal/admin-portal').then(m => m.AdminPortal),
  },
  {
    path: 'company',
    canActivate: [companyGuard],
    loadComponent: () =>
      import('./shared/layouts/company-layout/company-layout').then(m => m.CompanyLayout),
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'complete-profile',
        loadComponent: () =>
          import('./features/company/complete-profile/complete-profile').then(m => m.CompleteProfile),
      },
      {
        path: 'subscription',
        loadComponent: () =>
          import('./features/company/subscription/subscription').then(m => m.Subscription),
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/company/dashboard/dashboard').then(m => m.Dashboard),
      },
      {
        path: 'jobs',
        loadComponent: () =>
          import('./features/company/company-jobs/company-jobs').then(m => m.CompanyJobs),
      },
      {
        path: 'jobs/new',
        loadComponent: () =>
          import('./features/company/company-post-job/company-post-job').then(m => m.CompanyPostJob),
      },
      {
        path: 'jobs/:jobPostId/applicants',
        loadComponent: () =>
          import('./features/company/company-job-applicants/company-job-applicants').then(m => m.CompanyJobApplicants),
      },
      {
        path: 'applications',
        loadComponent: () =>
          import('./features/company/company-applications/company-applications').then(m => m.CompanyApplications),
      },
      {
        path: 'billing/assessment',
        loadComponent: () =>
          import('./features/company/company-billing-assessment/company-billing-assessment').then(m => m.CompanyBillingAssessment),
      },
      {
        path: 'assessments',
        loadComponent: () =>
          import('./features/company/company-assessments/company-assessments').then(m => m.CompanyAssessments),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./features/company/company-profile/company-profile').then(m => m.CompanyProfile),
      },
      {
        path: 'assessments/create',
        loadComponent: () =>
          import('./features/company/company-assessment-create/company-assessment-create').then(m => m.CompanyAssessmentCreate),
      },
      {
        path: 'assessments/create/browse',
        loadComponent: () =>
          import('./features/company/company-assessment-browse/company-assessment-browse').then(m => m.CompanyAssessmentBrowse),
      },
      {
        path: 'assessments/:id/results',
        loadComponent: () =>
          import('./features/company/company-assessment-results/company-assessment-results').then(m => m.CompanyAssessmentResults),
      },
      {
        path: 'walkin',
        loadComponent: () =>
          import('./features/company/company-walkin/company-walkin').then(m => m.CompanyWalkin),
      },
      {
        path: 'walkin/new',
        loadComponent: () =>
          import('./features/company/company-walkin-request/company-walkin-request').then(m => m.CompanyWalkinRequest),
      },
      {
        path: 'walkin/:requestId/candidates',
        loadComponent: () =>
          import('./features/company/company-walkin-candidates/company-walkin-candidates').then(m => m.CompanyWalkinCandidates),
      },
      {
        path: 'walkin/:requestId/payment',
        loadComponent: () =>
          import('./features/company/company-walkin-payment/company-walkin-payment').then(m => m.CompanyWalkinPayment),
      },
    ],
  },
  {
    path: 'trainer',
    canActivate: [trainerGuard],
    loadComponent: () =>
      import('./shared/layouts/trainer-layout/trainer-layout').then(m => m.TrainerLayout),
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/trainer/trainer-dashboard/trainer-dashboard').then(m => m.TrainerDashboard),
      },
    ],
  },
  {
    path: 'coordinator',
    canActivate: [coordinatorGuard],
    loadComponent: () =>
      import('./shared/layouts/coordinator-layout/coordinator-layout').then(m => m.CoordinatorLayout),
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/coordinator/dashboard/dashboard').then(m => m.Dashboard),
      },
      {
        path: 'walkins',
        loadComponent: () =>
          import('./features/coordinator/walkins/walkins').then(m => m.Walkins),
      },
      {
        path: 'walkins/:eventId/applicants',
        loadComponent: () =>
          import('./features/coordinator/walkin-applicants/walkin-applicants').then(m => m.WalkinApplicants),
      },
    ],
  },
  {
    path: 'assessment/invite/:token',
    loadComponent: () =>
      import('./features/assessment/assessment').then(m => m.Assessment),
  },
  // ── Walk-in candidate invite — public, no login. The qrToken IS the credential ──
  {
    path: 'walkin/invite/:qrToken',
    loadComponent: () =>
      import('./features/public/walkin-invite/walkin-invite').then(m => m.WalkinInvite),
  },
  // ── Jobs: user list/detail (auth) + public company/walkin pages (no auth) ──
  {
    path: 'jobs',
    loadComponent: () =>
      import('./shared/layouts/user-layout/user-layout').then(m => m.UserLayout),
    children: [
      { path: '', canActivate: [authGuard], loadComponent: () => import('./features/user/jobs/jobs').then(m => m.Jobs) },
      { path: 'company/:shareCode', loadComponent: () => import('./features/public/company-job-detail/company-job-detail').then(m => m.CompanyJobDetail) },
      { path: 'walkin/:shareCode', loadComponent: () => import('./features/public/walkin-event-detail/walkin-event-detail').then(m => m.WalkinEventDetail) },
      { path: ':id', canActivate: [authGuard], loadComponent: () => import('./features/user/job-detail/job-detail').then(m => m.JobDetail) },
    ],
  },
  // ── Authenticated user routes — no /user/ prefix ──────────────────────────
  {
    path: '',
    loadComponent: () =>
      import('./shared/layouts/user-layout/user-layout').then(m => m.UserLayout),
    children: [
      { path: 'dashboard', canActivate: [authGuard], loadComponent: () => import('./features/user/dashboard/dashboard').then(m => m.Dashboard) },
      { path: 'profile', canActivate: [authGuard], loadComponent: () => import('./features/user/profile/profile').then(m => m.Profile) },
      { path: 'profile-setup', canActivate: [authGuard], loadComponent: () => import('./features/user/profile-setup/profile-setup').then(m => m.ProfileSetup) },
      { path: 'post-job', canActivate: [authGuard], loadComponent: () => import('./features/user/post-job/post-job').then(m => m.PostJob) },
      { path: 'my-jobs', canActivate: [authGuard], loadComponent: () => import('./features/user/my-jobs/my-jobs').then(m => m.MyJobs) },
      { path: 'my-jobs/:jobId/applicants', canActivate: [authGuard], loadComponent: () => import('./features/user/job-applicants/job-applicants').then(m => m.JobApplicants) },
      { path: 'applications', canActivate: [authGuard], loadComponent: () => import('./features/user/applications/applications').then(m => m.Applications) },
      { path: 'referrals', canActivate: [authGuard], loadComponent: () => import('./features/user/referrals/referrals').then(m => m.Referrals) },
      { path: 'availability', canActivate: [authGuard], loadComponent: () => import('./features/user/availability/availability').then(m => m.Availability) },
      { path: 'referrer-requests', canActivate: [authGuard], loadComponent: () => import('./features/user/referrer-requests/referrer-requests').then(m => m.ReferrerRequests) },
      { path: 'walkin-events', canActivate: [authGuard], loadComponent: () => import('./features/user/walkin-events/walkin-events').then(m => m.WalkinEvents) },
      { path: 'meeting/:meetingId', canActivate: [authGuard], loadComponent: () => import('./features/user/meeting/meeting').then(m => m.MeetingPage) },
      { path: 'session-feedback/:meetingId', canActivate: [authGuard], loadComponent: () => import('./features/user/session-feedback/session-feedback').then(m => m.SessionFeedback) },
      { path: 'session-confirm/:meetingId', canActivate: [authGuard], loadComponent: () => import('./features/user/session-confirm/session-confirm').then(m => m.SessionConfirm) },
      { path: 'session-done/:meetingId', canActivate: [authGuard], loadComponent: () => import('./features/user/session-done/session-done').then(m => m.SessionDone) },
      { path: 'dispute/:meetingId', canActivate: [authGuard], loadComponent: () => import('./features/user/dispute/dispute').then(m => m.Dispute) },
      { path: 'connect/:referralId', canActivate: [authGuard], loadComponent: () => import('./features/user/connect/connect').then(m => m.Connect) },
      { path: 'reschedule/:requestId', canActivate: [authGuard], loadComponent: () => import('./features/user/reschedule-accept/reschedule-accept').then(m => m.RescheduleAccept) },
      { path: 'sessions', canActivate: [authGuard], loadComponent: () => import('./features/user/sessions/sessions').then(m => m.Sessions) },
      { path: 'purchases', canActivate: [authGuard], loadComponent: () => import('./features/user/purchases/purchases').then(m => m.Purchases) },
      { path: 'purchases/:orderId', canActivate: [authGuard], loadComponent: () => import('./features/user/purchases/purchase-detail').then(m => m.PurchaseDetail) },
      { path: 'payouts', canActivate: [authGuard], loadComponent: () => import('./features/user/payout-setup/payout-setup').then(m => m.PayoutSetup) },
      // Older links (dashboard banner, emails already sent) still land on the same page
      { path: 'payout/bank-details', redirectTo: 'payouts', pathMatch: 'full' },
      // Specialization profile — disabled until the feature has real content and a public
      // launch is ready. Component code is untouched; re-enable by uncommenting these two lines.
      // { path: 'specialization/:trackSlug', canActivate: [authGuard], loadComponent: () => import('./features/user/specialization/specialization').then(m => m.Specialization) },
      // { path: 'specialization/:trackSlug/roadmap', canActivate: [authGuard], loadComponent: () => import('./features/user/specialization/roadmap/roadmap').then(m => m.Roadmap) },
      // ── Legacy redirects ──
      { path: 'user/problems', redirectTo: '/problems', pathMatch: 'full' },
      { path: 'user/problems/:slug', redirectTo: '/problems/:slug/description', pathMatch: 'full' },
    ],
  },
  {
    path: 'admin',
    canActivate: [adminGuard],
    loadComponent: () =>
      import('./shared/layouts/admin-layout/admin-layout').then(m => m.AdminLayout),
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/admin/admin-dashboard/admin-dashboard').then(m => m.AdminDashboard),
      },
      {
        path: 'requests',
        loadComponent: () =>
          import('./features/admin/admin-requests/admin-requests').then(m => m.AdminRequests),
      },
      {
        path: 'companies',
        loadComponent: () =>
          import('./features/admin/admin-companies/admin-companies').then(m => m.AdminCompanies),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./features/admin/admin-users/admin-users').then(m => m.AdminUsers),
      },
      {
        path: 'jobs',
        loadComponent: () =>
          import('./features/admin/admin-jobs/admin-jobs').then(m => m.AdminJobs),
      },
      {
        path: 'disputes',
        loadComponent: () =>
          import('./features/admin/admin-disputes/admin-disputes').then(m => m.AdminDisputes),
      },
      {
        path: 'assessment-problems',
        loadComponent: () =>
          import('./features/admin/admin-assessment-problems/admin-assessment-problems').then(m => m.AdminAssessmentProblems),
      },
      {
        path: 'assessment-problems/add',
        loadComponent: () =>
          import('./features/admin/admin-assessment-problem-form/admin-assessment-problem-form').then(m => m.AdminAssessmentProblemForm),
      },
      {
        path: 'assessment-problems/edit/:id',
        loadComponent: () =>
          import('./features/admin/admin-assessment-problem-form/admin-assessment-problem-form').then(m => m.AdminAssessmentProblemForm),
      },
    ],
  },
  {
    path: 'rx-mgmt/9c4f2e',
    canActivate: [superAdminGuard],
    loadComponent: () =>
      import('./shared/layouts/super-admin-layout/super-admin-layout').then(m => m.SuperAdminLayout),
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/super-admin/dashboard/dashboard').then(m => m.Dashboard),
      },
      {
        path: 'admins',
        loadComponent: () =>
          import('./features/super-admin/admins/admins').then(m => m.Admins),
      },
      {
        path: 'companies',
        loadComponent: () =>
          import('./features/super-admin/company-hub/company-hub').then(m => m.CompanyHub),
      },
      {
        path: 'companies/:id',
        loadComponent: () =>
          import('./features/super-admin/company-detail/company-detail').then(m => m.CompanyDetail),
      },
      {
        path: 'companies/:id/jobs',
        loadComponent: () =>
          import('./features/super-admin/company-jobs/company-jobs').then(m => m.CompanyJobs),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./features/super-admin/users/users').then(m => m.Users),
      },
      {
        path: 'jobs',
        loadComponent: () =>
          import('./features/super-admin/jobs/jobs').then(m => m.Jobs),
      },
      {
        path: 'problems',
        loadComponent: () =>
          import('./features/super-admin/problems/problems').then(m => m.Problems),
      },
      {
        path: 'problems/add',
        loadComponent: () =>
          import('./features/super-admin/add-problem/add-problem').then(m => m.AddProblem),
      },
      {
        path: 'problems/playground',
        loadComponent: () =>
          import('./features/super-admin/problem-playground/problem-playground').then(m => m.ProblemPlayground),
      },
      {
        path: 'topics',
        loadComponent: () =>
          import('./features/super-admin/topics/topics').then(m => m.Topics),
      },
      {
        path: 'interview-bank',
        loadComponent: () =>
          import('./features/super-admin/interview-bank/interview-bank').then(m => m.InterviewBank),
      },
      {
        path: 'assessment-problems',
        loadComponent: () =>
          import('./features/super-admin/assessment-problems/assessment-problems').then(m => m.AssessmentProblems),
      },
      {
        path: 'assessment-problems/add',
        loadComponent: () =>
          import('./features/super-admin/add-assessment-problem/add-assessment-problem').then(m => m.AddAssessmentProblem),
      },
      {
        path: 'assessment-problems/edit/:id',
        loadComponent: () =>
          import('./features/super-admin/add-assessment-problem/add-assessment-problem').then(m => m.AddAssessmentProblem),
      },
      {
        path: 'walkins',
        loadComponent: () =>
          import('./features/super-admin/walkins/walkins').then(m => m.Walkins),
      },
      {
        path: 'walkins/:requestId/candidates',
        loadComponent: () =>
          import('./features/super-admin/walkin-candidates/walkin-candidates').then(m => m.WalkinCandidates),
      },
      {
        path: 'trainers',
        loadComponent: () =>
          import('./features/super-admin/trainers/trainers').then(m => m.Trainers),
      },
      {
        path: 'coordinators',
        loadComponent: () =>
          import('./features/super-admin/coordinators/coordinators').then(m => m.Coordinators),
      },
    ],
  },
  // ── Admin problem test — full screen, no sidebar ────────────────
  {
    path: 'rx-mgmt/9c4f2e/test/:id',
    canActivate: [superAdminGuard],
    loadComponent: () =>
      import('./features/super-admin/admin-problem-solve/admin-problem-solve').then(m => m.AdminProblemSolve),
  },
  // ── Assessment-pool problem test — full screen, no sidebar ──────
  {
    path: 'rx-mgmt/9c4f2e/assessment-problems/test/:id',
    canActivate: [superAdminGuard],
    loadComponent: () =>
      import('./features/super-admin/assessment-problem-solve/assessment-problem-solve').then(m => m.AssessmentProblemSolve),
  },
  // ── Clean username URLs — e.g. referredx.com/vikas ──────────────
  {
    path: ':username',
    loadComponent: () =>
      import('./features/user/public-profile/public-profile').then(m => m.PublicProfile),
  },
  {
    path: '**',
    loadComponent: () =>
      import('./features/public/not-found/not-found').then(m => m.NotFound),
  },
];